import { type Accessor, createMemo } from "solid-js";
import { api, createApiQuery, fetchAllPages } from "@/lib/api";
import { type components, queryKeys } from "@bso/shared";
import { useSession } from "@/lib/auth";

type Invitation = components["schemas"]["InvitationDTO"];

/**
 * Reactive count of pending organization invitations, used as the in-app
 * notification badge (legacy parity: notification-header-button).
 */
export function usePendingInvitationsCount(): Accessor<number> {
  const session = useSession();

  const invitations = createApiQuery<Invitation[] | null, components["schemas"]["ErrorModel"]>(
    () => queryKeys.auth.invitations(),
    async () =>
      fetchAllPages((query) => api.GET("/api/org/listInvitations", { params: { query } })),
    () => ({ enabled: session.isAuthenticated() }),
  );

  const pendingCount = createMemo(() => {
    if (!session.isAuthenticated()) {
      return 0;
    }
    // P23 / CC-60 · I2: this counter lives in SIDEBAR, i.e. in the
    // shell; unguarded read here suspends entire shell, not just
    // pantalla.
    if (!invitations.isSuccess) {
      return 0;
    }
    return invitations.data?.filter((inv) => inv.status === "pending").length ?? 0;
  });

  return pendingCount;
}
